import React from 'react';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FiArrowLeft,
  FiArrowRight,
  FiAward,
  FiBookOpen,
  FiBriefcase,
  FiChevronLeft,
  FiClock,
  FiDownload,
  FiExternalLink,
  FiFileText,
  FiInfo,
  FiList,
  FiLock,
  FiRefreshCw,
  FiStar,
  FiTarget,
  FiTool,
  FiUpload,
  FiX,
} from 'react-icons/fi';
import { compactDate } from './pathwayData';
import { useLearningCopy } from './learningLocale';
import { useLanguage } from '../contexts/LanguageContext';

const START_IMAGE = '/learning-pathways/start-pathway.png';
const CONGRATS_IMAGE = '/learning-pathways/congrats-page-pathway.png';

function courseProgressId(stepNumber, course, index) {
  return `${stepNumber}-${index}-${course.link || course.course_name}`;
}

const GENERATION_STEPS = [
  'Analyzing your profile...',
  'Building your pathway...',
  'Finding the best courses...',
  'Creating practice tasks...',
  'Saving your pathway...',
  'Almost ready!',
];

function stageIndex(stage) {
  if (stage === 'queued') return 0;
  if (stage === 'reading_cv') return 0;
  if (stage === 'building_pathway') return 1;
  if (stage === 'finding_courses_and_projects') return 2;
  if (stage === 'resolving_tools') return 3;
  if (stage === 'finalizing') return 4;
  if (stage === 'completed') return 5;
  return 3;
}

function BackButton({ label = 'Back', onClick }) {
  const copy = useLearningCopy();
  if (!onClick) return null;
  return (
    <button type="button" className="js-lp-back" onClick={onClick}>
      <FiChevronLeft className="js-icon--directional" aria-hidden="true" />
      <span>{copy(label)}</span>
    </button>
  );
}

export function StepBadge({ number, muted = false }) {
  const copy = useLearningCopy();
  return <span className={`js-lp-step-badge ${muted ? 'is-muted' : ''}`}>{copy('STEP')} {number}</span>;
}

function StatusIcon({ status }) {
  if (status === 'complete') return <FontAwesomeIcon icon={faCheck} className="is-complete" aria-hidden="true" />;
  if (status === 'locked') return <FiLock className="is-locked" aria-hidden="true" />;
  return <FiClock className="is-progress" aria-hidden="true" />;
}

function CompletedCourseSummary({ course, fallbackTitle }) {
  const copy = useLearningCopy();
  const title = course?.course_name || fallbackTitle || copy('Completed course');
  const content = course?.link ? (
    <a href={course.link} target="_blank" rel="noreferrer">{title}</a>
  ) : (
    <span>{title}</span>
  );

  return (
    <div className="js-lp-completed-course">
      <FontAwesomeIcon icon={faCheck} className="is-complete" aria-hidden="true" />
      {content}
    </div>
  );
}

function Field({ label, optional, children }) {
  const copy = useLearningCopy();
  return (
    <label className="js-lp-field">
      <span>
        {copy(label)}
        {optional && <small>{copy('optional')}</small>}
      </span>
      {children}
    </label>
  );
}

function displayCoursePricing(value) {
  const label = String(value || '').trim();
  const normalized = label.toLowerCase().replace(/\s+/g, ' ');
  if (!label || (normalized.startsWith('see ') && normalized.endsWith('vider'))) {
    return 'Check site';
  }
  return label;
}

export function PathwayForm({
  form,
  setForm,
  hasCv,
  cvFile,
  onFileChange,
  onSubmit,
  working,
  error,
  completedPathways,
  onOpenCompleted,
}) {
  const copy = useLearningCopy();
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const formValid = Boolean(
    form.targetJob?.trim()
    && form.industry?.trim()
    && form.coursePreference?.trim(),
  );

  return (
    <section className="js-lp-panel js-lp-form-panel" aria-labelledby="learning-pathways-title">
      <div className="js-lp-form-layout">
        <aside className="js-lp-form-intro" aria-label={copy('Learning pathway flow')}>
          <div className="js-lp-kicker">
            <FiBookOpen aria-hidden="true" />
            <span>{copy('Learning Pathways')}</span>
          </div>
          <h1 id="learning-pathways-title">{copy("LET'S GET STARTED")}</h1>
          <p>{copy('Tell us about your career goals so we can create your personalized learning pathway.')}</p>
          <div className="js-lp-form-steps" aria-label={copy('Learning pathway flow')}>
            <span className="is-active">{copy('Goal')}</span>
            <span>{copy('Pathway')}</span>
            <span>{copy('Rewards')}</span>
          </div>
          {completedPathways.length > 0 && (
            <button type="button" className="js-lp-secondary" onClick={onOpenCompleted}>
              <FiAward aria-hidden="true" />
              <span>{copy('Completed Pathways')}</span>
            </button>
          )}
        </aside>

        <div className="js-lp-question-stack">
          <Field label="What is your target job?">
            <input
              value={form.targetJob}
              onChange={(event) => update('targetJob', event.target.value)}
              placeholder={copy('e.g. Software Developer, Data Scientist, Product Manager')}
            />
          </Field>
          <Field label="Which industry/type of companies are you aiming for?">
            <input
              value={form.industry}
              onChange={(event) => update('industry', event.target.value)}
              placeholder={copy('e.g. Big Tech, Startups, Healthcare, Finance')}
            />
          </Field>
          <Field label="Do you want a pathway with free or paid courses?">
            <select value={form.coursePreference} onChange={(event) => update('coursePreference', event.target.value)}>
              <option value="">{copy('Choose one')}</option>
              <option value="Free courses only">{copy('Free courses only')}</option>
              <option value="Free and paid courses">{copy('Free and paid courses')}</option>
              <option value="Paid courses are okay">{copy('Paid courses are okay')}</option>
            </select>
          </Field>
          <Field label="Name some target companies of yours" optional>
            <input
              value={form.targetCompanies}
              onChange={(event) => update('targetCompanies', event.target.value)}
              placeholder={copy('e.g. Google, Meta, Apple, Amazon, Microsoft')}
            />
          </Field>

          {!hasCv && (
            <div className="js-lp-upload">
              <FiFileText aria-hidden="true" />
              <div>
                <strong>{copy('Upload a PDF CV to personalize this pathway.')}</strong>
                <input type="file" accept="application/pdf" onChange={onFileChange} />
                {cvFile && <small>{cvFile.name}</small>}
              </div>
            </div>
          )}

          {error && <p className="js-lp-error">{error}</p>}

          <button type="button" className="js-lp-primary js-lp-create-pathway-action" onClick={onSubmit} disabled={working || !formValid}>
            <span>{working ? copy('Creating') : copy('Create My Pathway')}</span>
            {working ? <FiRefreshCw className="is-spinning" aria-hidden="true" /> : <FiArrowRight className="js-icon--directional" aria-hidden="true" />}
          </button>
        </div>
      </div>
    </section>
  );
}

export function LoadingPanel({
  icon = <FiRefreshCw />,
  title,
  message,
  stage,
  stageText,
  statusMessage,
  progress,
  survey,
  canLeave = false,
  onBrowseJobs,
  onCareerCoach,
}) {
  const copy = useLearningCopy();
  const activeIndex = stageIndex(stage);
  const progressValue = Number(progress?.progress || 0);
  const visibleProgress = Math.max(0, Math.min(100, progressValue));
  return (
    <section className={`js-lp-panel js-lp-loading ${survey ? 'has-survey' : ''}`} aria-live="polite">
      <div className="js-lp-loading-main">
        <div className="js-lp-loader">
          <span />
          {React.cloneElement(icon, { 'aria-hidden': true })}
        </div>
        <h1>{title}</h1>
        <p>{message}</p>
        {survey && (
          <>
            <strong>{stageText || copy(GENERATION_STEPS[activeIndex])}</strong>
            {statusMessage && <p className="js-lp-loading-status">{statusMessage}</p>}
            {visibleProgress > 0 && (
              <div className="js-lp-progress-meter" aria-label={`Generation ${visibleProgress}% complete`}>
                <span style={{ width: `${visibleProgress}%` }} />
              </div>
            )}
            <div className="js-lp-dots" aria-hidden="true">
              {GENERATION_STEPS.map((step, index) => (
                <i key={step} className={index <= activeIndex ? 'is-active' : ''} />
              ))}
            </div>
          </>
        )}
        {!survey && (stageText || stage) && <strong>{stageText || stage}</strong>}
        {canLeave && (
          <div className="js-lp-leave-note">
            <p>{copy('You can leave this page while this runs. We’ll keep working in the background and the Learning Paths tab will show progress.')}</p>
            <div className="js-lp-actions">
              {onBrowseJobs && (
                <button type="button" className="js-lp-secondary" onClick={onBrowseJobs}>
                  <FiBriefcase aria-hidden="true" />
                  <span>{copy('Browse jobs')}</span>
                </button>
              )}
              {onCareerCoach && (
                <button type="button" className="js-lp-secondary" onClick={onCareerCoach}>
                  <FiBookOpen aria-hidden="true" />
                  <span>{copy('Career Coach')}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {survey && (
        <aside className="js-lp-while-working">
          <h2>{copy("While we're working...")}</h2>
          <div>
            <FiTarget aria-hidden="true" />
            <span>
              <strong>{copy('Target Role')}</strong>
              {survey.targetJob || copy('Learning pathway')}
            </span>
          </div>
          <div>
            <FiBriefcase aria-hidden="true" />
            <span>
              <strong>{copy('Industry Focus')}</strong>
              {survey.industry || copy('Not specified')}
            </span>
          </div>
          <div>
            <FiBookOpen aria-hidden="true" />
            <span>
              <strong>{copy('Course Preference')}</strong>
              {survey.coursePreference || copy('Not specified')}
            </span>
          </div>
          {survey.targetCompanies && (
            <div>
              <FiStar aria-hidden="true" />
              <span>
                <strong>{copy('Target Companies')}</strong>
                {survey.targetCompanies}
              </span>
            </div>
          )}
        </aside>
      )}
    </section>
  );
}

export function CvDecodedPanel({ pathwayData, onNext, onBack }) {
  const copy = useLearningCopy();
  const strengths = pathwayData?.strengths || [];
  const areas = pathwayData?.areasToImprove || [];
  return (
    <section className="js-lp-panel js-lp-decoded">
      <BackButton label="Back" onClick={onBack} />
      <h1>{copy('Your CV, Decoded')}</h1>
      <p>{copy('Hi there! I analyzed your CV and matched it to what employers are looking for in your target role.')}</p>

      <div className="js-lp-decoded-grid">
        <SectionList title={copy('Your Strengths')} items={strengths} tone="strength" />
        <SectionList title={copy('Areas to Improve')} items={areas} tone="gap" />
      </div>

      {pathwayData?.motivatingFeedback && <blockquote>{pathwayData.motivatingFeedback}</blockquote>}
      <div className="js-lp-actions is-right">
        <button type="button" className="js-lp-primary" onClick={onNext}>
          <span>{copy('Next')}</span>
          <FiArrowRight className="js-icon--directional" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

function SectionList({ title, items, tone }) {
  return (
    <div className={`js-lp-section-list is-${tone}`}>
      <h2>{title}</h2>
      {items.map((item) => (
        <div key={item}>
          <i />
          <span>{item}</span>
        </div>
      ))}
    </div>
  );
}

export function PathwayOverviewPanel({ steps, currentIndex, setCurrentIndex, onStart }) {
  const copy = useLearningCopy();
  const isLast = currentIndex >= steps.length - 1;
  const moveNext = () => {
    if (isLast) {
      onStart();
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <section className="js-lp-panel js-lp-overview">
      <h1>{copy('Your Pathway Overview')}</h1>
      <p>{copy("Here's the journey we've built for you, one skill at a time, in the order that actually makes sense.")}</p>
      <div className="js-lp-overview-list">
        {steps.map((step, index) => {
          const expanded = index === currentIndex;
          return (
            <button
              key={`${step.number}-${step.title}`}
              type="button"
              className={expanded ? 'is-expanded' : ''}
              onClick={() => setCurrentIndex(index)}
            >
              <StepBadge number={step.number} muted={!expanded} />
              <div>
                <h2>{step.title}</h2>
                {expanded && (
                  <>
                    <p>{step.whatYouLearn}</p>
                    <blockquote>{step.whyYouShouldLearnIt}</blockquote>
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>
      <div className="js-lp-actions js-lp-overview-actions">
        {currentIndex > 0 && (
          <button type="button" className="js-lp-secondary" onClick={() => setCurrentIndex(currentIndex - 1)}>
            <FiArrowLeft className="js-icon--directional" aria-hidden="true" />
            <span>{copy('Previous')}</span>
          </button>
        )}
        <button type="button" className="js-lp-primary" onClick={moveNext}>
          <span>{copy(isLast ? 'Start Pathway' : 'Next')}</span>
          <FiArrowRight className="js-icon--directional" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

export function PathwayIntroPanel({ onBegin }) {
  const copy = useLearningCopy();
  return (
    <section className="js-lp-panel js-lp-illustrated js-lp-intro-card">
      <img src={START_IMAGE} alt="" />
      <div>
        <h1>{copy('Your Pathway Awaits!')}</h1>
        <p>{copy("You've got a solid base. It's time to build on it.")}</p>
        <p>{copy("I'll guide you through each step, one thing at a time.")}</p>
        <p>{copy("You're closer to your dream career than you think. Let's get started!")}</p>
        <button type="button" className="js-lp-primary js-lp-wide-action" onClick={onBegin}>
          <span>{copy("Let's begin!")}</span>
        </button>
      </div>
    </section>
  );
}

export function StartStepPanel({ step, assessed = false, onBegin }) {
  const copy = useLearningCopy();
  const [showWhy, setShowWhy] = React.useState(false);
  if (!step) return null;
  return (
    <section className="js-lp-panel js-lp-start">
      <h1>{copy('NOW STARTING')}</h1>
      <StepBadge number={step.number} />
      <h2>{step.title}</h2>
      <button type="button" className="js-lp-inline-link" onClick={() => setShowWhy(true)}>
        <FiInfo aria-hidden="true" />
        <span>{copy('Why this step?')}</span>
      </button>

      <div className="js-lp-reward-box is-preview">
        <FiAward aria-hidden="true" />
        <div>
          {assessed && step.type !== 'tools' ? (
            <>
              <strong>{copy('Pass the final task and answer three short questions')}</strong>
              <p className="js-lp-reward-copy">{copy('If you pass, you can earn the skill badge and a pathway certificate after every required step.')} <em>{step.profileTag}</em></p>
            </>
          ) : assessed ? (
            <>
              <strong>{copy('Finish the learning materials')}</strong>
              <p className="js-lp-reward-copy">{copy('Finishing these materials moves you to the next step. It does not prove that you can use the tool at work.')}</p>
            </>
          ) : (
            <>
              <strong>{copy("Upon completing this step, you'll unlock")}</strong>
              <ol>
                <li><b>1</b><span className="js-lp-reward-copy">{copy('A profile badge:')} <em>{step.profileTag}</em></span></li>
                <li><b>2</b><span>A downloadable certificate of completion titled &quot;{step.title}&quot;</span></li>
              </ol>
            </>
          )}
        </div>
      </div>

      <div className="js-lp-actions">
        <button type="button" className="js-lp-primary js-lp-wide-action" onClick={onBegin}>
          <span>{copy("Let's go")}</span>
          <FiArrowRight className="js-icon--directional" aria-hidden="true" />
        </button>
      </div>

      {showWhy && (
        <WhyStepModal step={step} onClose={() => setShowWhy(false)} />
      )}
    </section>
  );
}

function WhyStepModal({ step, onClose }) {
  const copy = useLearningCopy();
  return (
    <div className="js-lp-modal" role="dialog" aria-modal="true" aria-label={copy('Why this step?')}>
      <button type="button" className="js-lp-modal-scrim" onClick={onClose} aria-label={copy('Close')} />
      <div className="js-lp-modal-card">
        <button type="button" className="js-lp-modal-close" onClick={onClose} aria-label={copy('Close')}>
          <FiX aria-hidden="true" />
        </button>
        <h2>{step.title}</h2>
        <h3>{copy("What you'll learn:")}</h3>
        <p>{step.whatYouLearn}</p>
        <h3>{copy('Why you should learn it:')}</h3>
        <p>{step.whyYouShouldLearnIt}</p>
        <button type="button" className="js-lp-primary" onClick={onClose}>{copy('Got it!')}</button>
      </div>
    </div>
  );
}

export function StepMaterialsPanel({
  step,
  assessed = false,
  steps,
  completedSteps,
  currentStepNumber,
  progress,
  onCourseOpen,
  onCourseProofSubmit,
  onCourseProofFallback,
  onCoursesComplete,
  onExerciseOpen,
  onViewPath,
  busyAction,
  error,
}) {
  const copy = useLearningCopy();
  const [showPath, setShowPath] = React.useState(false);
  const [proofFile, setProofFile] = React.useState(null);
  const [proofUrl, setProofUrl] = React.useState('');
  const [replacingProof, setReplacingProof] = React.useState(false);
  const [showProofDialog, setShowProofDialog] = React.useState(false);
  const [proofVerificationError, setProofVerificationError] = React.useState('');
  if (!step) return null;
  const coursesDone = Boolean(progress?.coursesCompleted);
  const hasClicked = Boolean(progress?.hasClickedAnyCourse);
  const selectedCourseId = progress?.selectedCourseId || '';
  const courseProof = progress?.courseProof || null;
  const selectedCourse = (step.courses || []).find((course, index) => (
    courseProgressId(step.number, course, index) === selectedCourseId
  ));
  const selectedVerificationMode = progress?.selectedCourseVerificationMode
    || selectedCourse?.verificationMode
    || 'certificate';
  const usesCertificateVerification = step.type !== 'tools' && selectedVerificationMode === 'certificate';
  const proofVerified = ['verified', 'submitted'].includes(courseProof?.status)
    && courseProof?.courseId === selectedCourseId;
  const needsProofSubmission = usesCertificateVerification && !proofVerified;
  const exercisesUnlocked = coursesDone && step.type !== 'tools';
  const exerciseOneDone = Boolean(progress?.exercise1Completed);
  const exerciseTwoDone = Boolean(progress?.exercise2Completed);
  const allExercisesDone = step.exercises?.length
    ? step.exercises.every((_, index) => (index === 0 ? exerciseOneDone : exerciseTwoDone))
    : false;
  const visibleExercises = (step.exercises || []).slice(0, 2);

  const submitProof = async () => {
    if (!onCourseProofSubmit || !selectedCourseId || (!proofFile && !proofUrl.trim())) return;
    setProofVerificationError('');
    try {
      const result = await onCourseProofSubmit({
        courseId: selectedCourseId,
        courseTitle: selectedCourse?.course_name || '',
        proofUrl: proofUrl.trim(),
        file: proofFile,
      });
      if (result?.proof?.status !== 'verified') {
        const reasons = result?.proof?.verification?.reasons || [];
        setProofVerificationError(reasons.join(' ') || copy("We couldn't verify this certificate."));
        return;
      }
      setProofFile(null);
      setProofUrl('');
      setReplacingProof(false);
      setShowProofDialog(false);
    } catch (_) {
      // The parent surfaces the backend error in this panel.
    }
  };

  const continueWithoutCertificate = async () => {
    if (!onCourseProofFallback) return;
    setProofVerificationError('');
    try {
      await onCourseProofFallback();
      setProofFile(null);
      setProofUrl('');
      setReplacingProof(false);
      setShowProofDialog(false);
    } catch (_) {
      // The parent surfaces the backend error in this panel.
    }
  };

  return (
    <section className="js-lp-panel js-lp-materials">
      <header className="js-lp-live-step-head">
        <StepBadge number={step.number} />
        <h1>{step.title}</h1>
        <button type="button" className="js-lp-inline-link" onClick={() => setShowPath(true)}>
          <FiList aria-hidden="true" />
          <span>{copy('View Path')}</span>
        </button>
      </header>

      <section className="js-lp-material-block">
        <div className="js-lp-block-head">
          <FiBookOpen aria-hidden="true" />
          <h3>{copy(step.type === 'tools' ? 'Official Learning Pages' : 'Top Courses & Certificates')}</h3>
          {!coursesDone && <StatusIcon status="progress" />}
        </div>
        {!coursesDone && step.type !== 'tools' && (
          <p className="js-lp-course-guidance">
            <strong>{copy('Choose ONE course to complete this step.')}</strong>
            {' '}You do not need to complete every course listed, though you are welcome to take more if you would like. The last course you open is treated as your selection.
          </p>
        )}
        {coursesDone ? (
          <CompletedCourseSummary
            course={selectedCourse || (!courseProof?.courseTitle ? step.courses?.[0] : null)}
            fallbackTitle={courseProof?.courseTitle}
          />
        ) : (
          <div className="js-lp-course-list">
            {(step.courses || []).map((course, index) => {
              const courseId = courseProgressId(step.number, course, index);
              const selected = courseId === selectedCourseId;
              return (
                <a
                  key={`${course.link}-${index}`}
                  href={course.link}
                  target="_blank"
                  rel="noreferrer"
                  className={selected ? 'is-selected' : ''}
                  aria-current={selected ? 'true' : undefined}
                  onClick={() => onCourseOpen(course, index)}
                >
                  <span>{course.course_name}</span>
                  <small>
                    {course.isSearchFallback
                      ? copy('Search page · Open it and choose one course')
                      : (
                        <>
                          {course.pricing ? displayCoursePricing(course.pricing) : copy('Check site')} · {course.rating || copy('Not listed')}
                          {' · '}
                          {(course.verificationMode || 'certificate') === 'certificate'
                            ? copy('Certificate check')
                            : copy('Verified through exercises')}
                        </>
                      )}
                    {selected ? ` · ${copy('Selected course')}` : ''}
                  </small>
                  <FiExternalLink aria-hidden="true" />
                </a>
              );
            })}
          </div>
        )}
        {!coursesDone && step.type !== 'tools' && proofVerified && !replacingProof && (
          <div className="js-lp-course-proof-submitted">
            <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
            <span>
              <strong>{copy('Certificate verified automatically')}</strong>
              {courseProof.fileName || courseProof.proofUrl || courseProof.courseTitle || copy('Saved proof')}
            </span>
            <button
              type="button"
              className="js-lp-inline-link"
              onClick={() => {
                setReplacingProof(true);
                setShowProofDialog(true);
              }}
            >
              {copy('Replace')}
            </button>
          </div>
        )}
        {!coursesDone && <div className="js-lp-course-complete">
          <button
            type="button"
            className={step.type === 'tools' ? 'js-lp-secondary is-contained' : 'js-lp-primary js-lp-course-complete-action'}
            onClick={needsProofSubmission ? () => setShowProofDialog(true) : onCoursesComplete}
            disabled={busyAction === 'courses' || busyAction === 'proof' || busyAction === 'proof-fallback' || (!hasClicked && step.type !== 'tools')}
          >
            {busyAction === 'proof'
                ? copy('Submitting proof...')
              : busyAction === 'courses'
                ? copy('Marking complete...')
                : needsProofSubmission
                  ? copy('Submit proof of completion')
                : step.type === 'tools'
                  ? copy('Finish Tool Learning')
                  : usesCertificateVerification
                    ? copy('Mark Selected Course Complete')
                    : copy('I finished this resource')}
          </button>
          {needsProofSubmission && (
            <small>{copy(hasClicked ? 'Submit your selected course proof to enable completion.' : 'Choose one course before submitting proof of completion.')}</small>
          )}
          {step.type !== 'tools' && !usesCertificateVerification && (
            <small>{copy('The pathway exercises will verify what you learned. No certificate is required.')}</small>
          )}
        </div>}
      </section>

      {step.type !== 'tools' && (
        <section className="js-lp-material-block">
            <div className="js-lp-block-head">
              <FiTool aria-hidden="true" />
              <h3>{copy(assessed ? 'Practice & final task' : 'Learn & practice')}</h3>
              <StatusIcon status={!exercisesUnlocked ? 'locked' : allExercisesDone ? 'complete' : 'progress'} />
            </div>
          {!exercisesUnlocked && (
            <p>
              {usesCertificateVerification
                ? assessed
                  ? copy('Send proof for one course to unlock the practice tasks.')
                  : copy('Verify one course certificate to unlock the practice tasks.')
                : assessed
                  ? copy('Finish your selected resource to unlock the practice tasks.')
                  : copy('Finish your selected resource to unlock the practice tasks. The exercises verify what you learned.')}
            </p>
          )}
          {visibleExercises.length > 0 && (
            <div className="js-lp-exercise-list">
              {visibleExercises.map((exercise, index) => {
                const exerciseIndex = index + 1;
                const done = exerciseIndex === 1 ? exerciseOneDone : exerciseTwoDone;
                const status = !exercisesUnlocked ? 'locked' : done ? 'complete' : 'progress';
                return (
                  <button
                    key={`${exercise.project}-${index}`}
                    type="button"
                    className={done ? 'is-complete' : ''}
                    onClick={() => onExerciseOpen(exercise, exerciseIndex)}
                    disabled={!exercisesUnlocked}
                  >
                    <strong>{exercise.project}</strong>
                    <span>
                      <StatusIcon status={status} />
                      {copy(!exercisesUnlocked ? 'Locked' : done ? 'Completed' : 'In Progress')}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}

      {error && <p className="js-lp-error">{error}</p>}

      {showPath && (
        <ViewPathModal
          steps={steps}
          currentStepNumber={currentStepNumber}
          completedSteps={completedSteps}
          onClose={() => setShowPath(false)}
          onViewStep={(stepNumber) => {
            setShowPath(false);
            onViewPath(stepNumber);
          }}
        />
      )}

      {showProofDialog && (
        <div className="js-lp-modal" role="dialog" aria-modal="true" aria-label={copy('Submit proof of completion')}>
          <button
            type="button"
            className="js-lp-modal-scrim"
            onClick={() => {
              setReplacingProof(false);
              setShowProofDialog(false);
            }}
            aria-label={copy('Close')}
          />
          <div className="js-lp-modal-card is-course-proof">
            <button
              type="button"
              className="js-lp-modal-close"
              onClick={() => {
                setReplacingProof(false);
                setShowProofDialog(false);
              }}
              aria-label={copy('Close')}
            >
              <FiX aria-hidden="true" />
            </button>
            <div className="js-lp-course-proof-modal-head">
              <FiUpload aria-hidden="true" />
              <div>
                <h2>{copy(replacingProof ? 'Replace proof of completion' : 'Submit proof of completion')}</h2>
                <p>
                  {selectedCourse
                    ? <><strong>{selectedCourse.course_name}</strong> — {copy('For your selected course, upload a certificate or screenshot, or add a public credential link.')}</>
                    : copy('Choose a course first, then upload a certificate or screenshot, or add a public credential link.')}
                </p>
              </div>
            </div>
            <div className="js-lp-course-proof-form is-modal">
              {proofVerificationError && (
                <div className="js-lp-course-proof-failed" role="alert">
                  <strong>{copy("We couldn't verify this certificate.")}</strong>
                  <span>{proofVerificationError}</span>
                </div>
              )}
              <label>
                <span>{copy('Certificate or screenshot')}</span>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                  aria-label={copy('Certificate or completion screenshot')}
                  onChange={(event) => setProofFile(event.target.files?.[0] || null)}
                />
                <small>{copy('PDF, PNG, or JPG · 10 MB maximum')}</small>
              </label>
              <span className="js-lp-course-proof-or">{copy('or')}</span>
              <label>
                <span>{copy('Public credential link')}</span>
                <input
                  type="url"
                  value={proofUrl}
                  placeholder="https://..."
                  aria-label={copy('Public credential link')}
                  onChange={(event) => setProofUrl(event.target.value)}
                />
              </label>
              <div className="js-lp-course-proof-modal-actions">
                <button
                  type="button"
                  className="js-lp-secondary"
                  onClick={continueWithoutCertificate}
                  disabled={busyAction === 'proof-fallback'}
                >
                  {busyAction === 'proof-fallback' ? copy('Switching') : copy('Continue without certificate')}
                </button>
                <button
                  type="button"
                  className="js-lp-primary"
                  onClick={submitProof}
                  disabled={busyAction === 'proof' || !selectedCourseId || (!proofFile && !proofUrl.trim())}
                >
                  {busyAction === 'proof' ? copy('Submitting') : copy(replacingProof ? 'Replace proof' : 'Submit proof')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function CompletedStepDetailPanel({ step, progress = {}, onBack }) {
  const copy = useLearningCopy();
  if (!step) return null;
  const coursesDone = Boolean(progress?.coursesCompleted);
  const exerciseOneDone = Boolean(progress?.exercise1Completed);
  const exerciseTwoDone = Boolean(progress?.exercise2Completed);
  const courseProof = progress?.courseProof || null;
  const selectedCourseId = progress?.selectedCourseId || courseProof?.courseId || '';
  const selectedCourse = (step.courses || []).find((course, index) => (
    courseProgressId(step.number, course, index) === selectedCourseId
  ));
  const allExercisesDone = step.exercises?.length
    ? step.exercises.every((_, index) => (index === 0 ? exerciseOneDone : exerciseTwoDone))
    : false;

  return (
    <section className="js-lp-panel js-lp-completed-step">
      <BackButton label="Go Back" onClick={onBack} />
      <StepBadge number={step.number} />
      <h1>{step.title}</h1>

      <section className="js-lp-material-block">
        <div className="js-lp-block-head">
          <FiBookOpen aria-hidden="true" />
          <h3>{copy(step.type === 'tools' ? 'Official Learning Pages' : 'Top Courses & Certificates')}</h3>
          {!coursesDone && <StatusIcon status="progress" />}
        </div>
        {coursesDone ? (
          <CompletedCourseSummary
            course={selectedCourse || (!courseProof?.courseTitle ? step.courses?.[0] : null)}
            fallbackTitle={courseProof?.courseTitle}
          />
        ) : <div className="js-lp-course-list">
          {(step.courses || []).map((course, index) => {
            const selected = courseProgressId(step.number, course, index) === selectedCourseId;
            return (
              <a
                key={`${course.link}-${index}`}
                href={course.link}
                target="_blank"
                rel="noreferrer"
                className={selected ? 'is-selected' : ''}
                aria-current={selected ? 'true' : undefined}
              >
                <span>{course.course_name}</span>
                <small>
                  {course.pricing ? displayCoursePricing(course.pricing) : copy('Check site')} · {course.rating || copy('Not listed')}
                  {selected ? ` · ${copy('Selected course')}` : ''}
                </small>
                <FiExternalLink aria-hidden="true" />
              </a>
            );
          })}
        </div>}
        {!coursesDone && ['verified', 'submitted'].includes(courseProof?.status) && (
          <div className="js-lp-course-proof-submitted">
            <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
            <span>
              <strong>{copy(courseProof.status === 'verified' ? 'Certificate verified automatically' : 'Proof submitted')}</strong>
              {courseProof.fileName || courseProof.proofUrl || courseProof.courseTitle || copy('Completion evidence saved')}
            </span>
            {courseProof.proofUrl && (
              <a className="js-lp-inline-link" href={courseProof.proofUrl} target="_blank" rel="noreferrer">{copy('View')}</a>
            )}
          </div>
        )}
      </section>

      {step.type !== 'tools' && (
        <section className="js-lp-material-block">
          <div className="js-lp-block-head">
            <FiTool aria-hidden="true" />
            <h3>{copy('Learn & Apply')}</h3>
            <StatusIcon status={allExercisesDone ? 'complete' : 'progress'} />
          </div>
          <div className="js-lp-readonly-exercises">
            {(step.exercises || []).slice(0, 2).map((exercise, index) => {
              const exerciseIndex = index + 1;
              const done = exerciseIndex === 1 ? exerciseOneDone : exerciseTwoDone;
              return (
                <div className="js-lp-readonly-exercise" key={`${exercise.project}-${index}`}>
                  <strong>{exercise.project}</strong>
                  <span className="js-lp-readonly-status">
                    <StatusIcon status={done ? 'complete' : 'progress'} />
                    {copy(done ? 'Completed' : 'In Progress')}
                  </span>
                  {exercise.description && (
                    <div className="js-lp-exercise-copy is-compact">
                      {splitLinks(exercise.description).map((part, partIndex) => (
                        part.href ? (
                          <a key={`${part.text}-${partIndex}`} href={part.href} target="_blank" rel="noreferrer">{part.text}</a>
                        ) : (
                          <p key={`${part.text}-${partIndex}`}>{part.text}</p>
                        )
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </section>
  );
}

function ViewPathModal({ steps, currentStepNumber, completedSteps, onClose, onViewStep }) {
  const copy = useLearningCopy();
  return (
    <div className="js-lp-modal" role="dialog" aria-modal="true" aria-label={copy('Pathway progress')}>
      <button type="button" className="js-lp-modal-scrim" onClick={onClose} aria-label={copy('Close')} />
      <div className="js-lp-modal-card is-path">
        <button type="button" className="js-lp-modal-close" onClick={onClose} aria-label={copy('Close')}>
          <FiX aria-hidden="true" />
        </button>
        <p>{copy('Tap any completed step to view details.')}</p>
        <div className="js-lp-progress-list">
          {steps.map((item) => {
            const completed = completedSteps.includes(item.number);
            const current = item.number === currentStepNumber;
            const locked = !completed && !current;
            return (
              <button
                type="button"
                key={`${item.number}-${item.title}`}
                disabled={!completed || current}
                onClick={() => onViewStep(item.number)}
              >
                <span>
                  <StepBadge number={item.number} muted={!current} />
                  <strong>{item.title}</strong>
                </span>
                <StatusIcon status={completed ? 'complete' : locked ? 'locked' : 'progress'} />
              </button>
            );
          })}
        </div>
        <button type="button" className="js-lp-primary" onClick={onClose}>{copy("Let's Keep Going!")}</button>
      </div>
    </div>
  );
}

function splitLinks(text) {
  const regex = /(https?:\/\/[^\s]+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, match.index).trim() });
    }
    const url = match[0].replace(/[),.;]+$/, '');
    const trailing = match[0].slice(url.length);
    parts.push({ text: url, href: url });
    if (trailing) parts.push({ text: trailing });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push({ text: text.slice(lastIndex).trim() });
  return parts.filter((part) => part.text);
}

export function StepCompletePanel({ reward, downloadUrl, onContinue }) {
  const copy = useLearningCopy();
  if (!reward) return null;
  return (
    <section className="js-lp-panel js-lp-complete">
      <h1>{copy('GREAT WORK!')}</h1>
      <StepBadge number={reward.stepNumber || 1} />
      <h2>{reward.stepTitle}</h2>
      <p className="js-lp-complete-line">
        <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
        <span>{copy('Complete!')}</span>
      </p>
      <h2>{copy(reward.credentialIssued === false || reward.credentialType === 'course_completion' ? 'Your Result' : 'Your Rewards')}</h2>
      <div className={downloadUrl ? 'js-lp-two-column' : ''}>
        {reward.badgeTitle && (
          <div className="js-lp-reward-box">
            <FiAward aria-hidden="true" />
            <div>
              <strong>{copy(reward.credentialIssued === false ? 'Assessment passed' : 'Assessed badge added to profile')}</strong>
              <em>{reward.badgeTitle}</em>
              {reward.credentialIssued === false && <span>{copy('Your demonstrated criteria are saved with this pathway.')}</span>}
            </div>
          </div>
        )}
        {reward.credentialType === 'course_completion' && (
          <div className="js-lp-reward-box">
            <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
            <div>
              <strong>{copy('Pathway progress updated')}</strong>
              <span>{copy('This tool-learning step records completion, not assessed proficiency.')}</span>
            </div>
          </div>
        )}
        {downloadUrl && (
          <div className="js-lp-reward-box">
            <FiAward aria-hidden="true" />
            <div>
              <strong>{copy('Certificate of completion')}</strong>
              <a href={downloadUrl} target="_blank" rel="noreferrer">
                <FiDownload aria-hidden="true" />
                <span>{copy('Tap to download')}</span>
              </a>
            </div>
          </div>
        )}
      </div>
      <div className="js-lp-actions">
        <button type="button" className="js-lp-primary js-lp-wide-action" onClick={onContinue}>
          <span>{copy("Let's keep going!")}</span>
        </button>
      </div>
    </section>
  );
}

export function PathwayProgressPanel({ steps, currentStepNumber, completedSteps, onContinue }) {
  const copy = useLearningCopy();
  const percentage = steps.length ? Math.min(100, Math.round((completedSteps.length / steps.length) * 100)) : 0;
  return (
    <section className="js-lp-panel js-lp-progress">
      <h1>{copy('YOUR PATHWAY')}</h1>
      <p>{percentage}% {copy('of this pathway is complete.')}</p>
      <div className="js-lp-progress-list">
        {steps.map((step) => {
          const completed = completedSteps.includes(step.number);
          const current = step.number === currentStepNumber;
          return (
            <div key={`${step.number}-${step.title}`}>
              <span>
                <StepBadge number={step.number} muted={!current} />
                <strong>{step.title}</strong>
              </span>
              <StatusIcon status={completed ? 'complete' : current ? 'progress' : 'locked'} />
            </div>
          );
        })}
      </div>
      <button type="button" className="js-lp-primary js-lp-wide-action" onClick={onContinue}>
        {copy("Let's Keep Going!")}
      </button>
    </section>
  );
}

export function PathwayCompletePanel({ assessed = false, credentialsIssued = false, onClaimRewards }) {
  const copy = useLearningCopy();
  return (
    <section className="js-lp-panel js-lp-illustrated js-lp-intro-card js-lp-congrats-card">
      <img src={CONGRATS_IMAGE} alt="" />
      <div>
        <h1>{copy('CONGRATS!')}</h1>
        <p>{copy('It took a lot of consistency and commitment to get here. You should be proud.')}</p>
        <p>{copy("You're a massive step closer to your dream career. Start applying now.")}</p>
        <button type="button" className="js-lp-primary js-lp-wide-action" onClick={onClaimRewards}>
          {copy(assessed && !credentialsIssued ? 'View results' : 'View credentials')}
        </button>
      </div>
    </section>
  );
}

export function FinalRewardsPanel({ badges, downloadUrl, assessed = false, credentialsIssued = false, onApply, onCompleted }) {
  const copy = useLearningCopy();
  return (
    <section className="js-lp-panel js-lp-final-rewards">
      <h1>{copy(assessed && !credentialsIssued ? 'YOUR RESULTS' : 'YOUR REWARDS')}</h1>
      <div className="js-lp-two-column">
        {assessed && !credentialsIssued ? (
          <div className="js-lp-reward-box">
              <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
              <div>
                <strong>{copy('Assessment results recorded')}</strong>
                <span>{copy('Your demonstrated criteria are saved with this pathway.')}</span>
              </div>
            </div>
        ) : (
          <RewardTags badges={badges} title={copy(assessed ? 'Assessed badges added to profile' : 'Tags added to profile')} />
        )}
        {(!assessed || credentialsIssued) && (
          <div className="js-lp-reward-box">
            <FiAward aria-hidden="true" />
            <div>
              <strong>{copy('Pathway certificate')}</strong>
              {downloadUrl ? (
                <a href={downloadUrl} target="_blank" rel="noreferrer">
                  <FiDownload aria-hidden="true" />
                  <span>{copy('Tap to download latest')}</span>
                </a>
              ) : (
                <span>{copy('Certificates are saved to your profile.')}</span>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="js-lp-actions">
        <button type="button" className="js-lp-primary" onClick={onApply}>{copy('Apply for jobs')}</button>
        <button type="button" className="js-lp-secondary" onClick={onCompleted}>{copy('Completed Pathways')}</button>
      </div>
    </section>
  );
}

function RewardTags({ badges, title }) {
  const copy = useLearningCopy();
  return (
    <div className="js-lp-reward-box">
      <FiAward aria-hidden="true" />
      <div>
        <strong>{title}</strong>
        <div className="js-lp-badge-grid">
          {(badges || []).map((badge) => <em key={badge}>{badge}</em>)}
          {(!badges || badges.length === 0) && <span>{copy('No profile tags found yet.')}</span>}
        </div>
      </div>
    </div>
  );
}

export function CompletedPathwaysPanel({ completedPathways, onStartNew, onBack, onOpenRewards }) {
  const copy = useLearningCopy();
  const { language } = useLanguage();
  return (
    <section className="js-lp-panel js-lp-history">
      <BackButton label="Back to home" onClick={onBack} />
      <h1>{copy('COMPLETED PATHWAYS')}</h1>
      <p>{copy('Your learning journey so far. Start a new pathway to keep growing!')}</p>
      <div className="js-lp-history-list">
        {completedPathways.map((pathway, index) => (
          <button key={pathway.id} type="button" onClick={() => onOpenRewards(pathway)}>
            <span>{copy('PathwayNumber', { number: index + 1 })}</span>
            <h2>{pathway.targetJob}</h2>
            {pathway.allBadges?.length > 0 && (
              <div className="js-lp-history-badges">
                {pathway.allBadges.slice(0, 2).map((badge) => <em key={badge}>{badge}</em>)}
                {pathway.allBadges.length > 2 && <small>+{pathway.allBadges.length - 2} {copy('more')}</small>}
              </div>
            )}
            <p>{copy('Completed on')} {compactDate(pathway.completedAt, language)}</p>
          </button>
        ))}
        {completedPathways.length === 0 && <div className="js-lp-empty">{copy('No completed pathways yet.')}</div>}
      </div>
      <div className="js-lp-actions">
        <button type="button" className="js-lp-primary js-lp-wide-action" onClick={onStartNew}>
          {copy('Start New Pathway')}
        </button>
      </div>
    </section>
  );
}

export function CompletedPathwayRewardsPanel({ pathway, onBack }) {
  const copy = useLearningCopy();
  if (!pathway) return null;
  return (
    <section className="js-lp-panel js-lp-final-rewards">
      <BackButton onClick={onBack} />
      <h1>{copy('YOUR REWARDS')}</h1>
      <p>{copy('From your {role} pathway', { role: pathway.targetJob })}</p>
      <div className="js-lp-two-column">
        <RewardTags badges={pathway.allBadges || []} title={copy('Tags added to profile')} />
        <div className="js-lp-reward-box">
          <FiAward aria-hidden="true" />
          <div>
            <strong>{copy('Certificates of completion')}</strong>
            <span>{copy('Saved certificate records stay attached to this completed pathway.')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
